import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import * as moment from 'moment-timezone';
import translate from '../../translate';
import {
  getPlanTimezone,
  getStartDateAndEndDateInterval,
} from '../../../services/dateUtilityFunctions';
import ConfigsContext from '../../../contextProviders/ConfigsContext';
import { dateWithoutTimeZone } from '../../../services/utilityFunctions';

function AsyncDateRange({
  t,
  id,
  startDate: sDate,
  endDate: eDate,
  timePicker = false,
  onChange,
  onClear,
  opens,
  placeholder,
  parentElId,
}) {
  const { organization } = useContext(ConfigsContext);
  const { directSelling } = organization;
  const planTimezone = getPlanTimezone(directSelling);

  // Keep moments in the plan's timezone when one is set (BRAVON-6325): their
  // wall dates then match what the user picks/sees, while .valueOf() keeps the
  // true UTC instants used to query.
  const toRangeMoment = value =>
    planTimezone ? moment(value).tz(planTimezone) : moment(value).utc();

  const [loading, setLoading] = useState(false);
  const pickerRef = useRef(null);

  const defaultRanges = useMemo(() => {
    const today = t('dateRange_today');
    const yesterday = t('dateRange_yesterday');
    const sevenDays = t('dateRange_sevenDays');
    const thirtyDays = t('dateRange_thirtyDays');
    const thisMonth = t('dateRange_thisMonth');
    const lastMonth = t('dateRange_lastMonth');

    return {
      [today]: [moment().utc().startOf('day'), moment().utc().endOf('day')],
      [yesterday]: [
        moment().utc().subtract(1, 'days').startOf('day'),
        moment().utc().subtract(1, 'days').endOf('day'),
      ],
      [sevenDays]: [
        moment().utc().subtract(6, 'days').startOf('day'),
        moment().utc().endOf('day'),
      ],
      [thirtyDays]: [
        moment().utc().subtract(29, 'days').startOf('day'),
        moment().utc().endOf('day'),
      ],
      [thisMonth]: getStartDateAndEndDateInterval(directSelling),
      [lastMonth]: getStartDateAndEndDateInterval(
        directSelling,
        moment().utc().subtract(1, 'month'),
      ),
    };
  }, [organization]);

  const [ranges, setRanges] = useState(defaultRanges);

  useEffect(() => {
    const fetchRanges = async () => {
      setLoading(true);
      try {
        const response = await axios.get(
          '/api/analytics/market-place/commissions/date-pickers/options',
          {
            headers: {
              'Accept-Version': '1.0.0',
              'X-Gamification-API': 'Games',
            },
          },
        );
        const { data } = response.data;
        const fetchedRanges = data.reduce((acc, range) => {
          acc[range.name] = [
            toRangeMoment(range.startDate),
            toRangeMoment(range.endDate),
            range.position,
            range.type,
          ];
          return acc;
        }, {});

        setRanges(fetchedRanges);
      } catch (error) {
        console.error('Error fetching date ranges:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRanges();
  }, []);

  const updateInputValue = (picker, startDate, endDate) => {
    // Format the start and end date
    const formattedStart = startDate.format('YYYY MMM DD');
    const formattedEnd = endDate.format('YYYY MMM DD');

    // Check if the start date and end date are the same (i.e., single day range)
    const range = startDate.isSame(endDate, 'day')
      ? formattedStart
      : `${formattedStart} - ${formattedEnd}`;

    // Set the formatted date range in the input field
    picker.find('.m-input').val(range);
  };

  useEffect(() => {
    const picker = $(`#${id}`);

    if (picker.length === 0) return;

    const start = sDate ? toRangeMoment(sDate) : toRangeMoment();
    const end = eDate ? toRangeMoment(eDate) : toRangeMoment();

    picker.daterangepicker({
      parentEl: `#${parentElId}`,
      autoUpdateInput: false,
      locale: {
        applyLabel: t('dateRange_apply'),
        cancelLabel: t('dateRange_clear'),
      },
      timePicker,
      startDate: start,
      endDate: end,
      opens,
      drops: 'auto',
      ranges,
    });

    updateInputValue(picker, start, end);

    picker.on('apply.daterangepicker', async (ev, element) => {
      let { startDate, endDate } = element;

      startDate = dateWithoutTimeZone(startDate);
      endDate = dateWithoutTimeZone(endDate);
      startDate = startDate.startOf('day');
      endDate = endDate.endOf('day');

      // Custom (manual) picks are interpreted as day boundaries in the plan's
      // timezone when one is configured (BRAVON-6325), matching the
      // backend-provided preset ranges. Preset selections below keep the exact
      // backend values.
      const customStart = planTimezone
        ? moment.tz(startDate.format('YYYY-MM-DD'), planTimezone).startOf('day')
        : startDate;
      const customEnd = planTimezone
        ? moment.tz(endDate.format('YYYY-MM-DD'), planTimezone).endOf('day')
        : endDate;

      const matchingRangeKey = Object.keys(ranges).find(key => {
        const [rangeStart, rangeEnd] = ranges[key];
        return (
          startDate.format('YYYY-MM-DD') === rangeStart.format('YYYY-MM-DD') &&
          endDate.format('YYYY-MM-DD') === rangeEnd.format('YYYY-MM-DD')
        );
      });

      const rangeType = matchingRangeKey
        ? ranges[matchingRangeKey][3]
        : 'custom';

      updateInputValue(picker, startDate, endDate);

      setLoading(true);
      try {
        console.log('*Selection of date', {
          startDate:
            ranges[matchingRangeKey]?.length > 0
              ? ranges[matchingRangeKey][0]?.valueOf()
              : customStart.valueOf(),
          endDate:
            ranges[matchingRangeKey]?.length > 0
              ? ranges[matchingRangeKey][1]?.valueOf()
              : customEnd.valueOf(),
          rangeType,
        });
        onChange({
          startDate:
            ranges[matchingRangeKey]?.length > 0
              ? ranges[matchingRangeKey][0]?.valueOf()
              : customStart.valueOf(),
          endDate:
            ranges[matchingRangeKey]?.length > 0
              ? ranges[matchingRangeKey][1]?.valueOf()
              : customEnd.valueOf(),
          rangeType,
        });
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    });

    picker.on('cancel.daterangepicker', async () => {
      picker.find('.m-input').val('');
      setLoading(true);
      try {
        await onClear();
      } catch (error) {
        console.error('Error clearing data:', error);
      } finally {
        setLoading(false);
      }
    });

    picker.on('show.daterangepicker', async () => {
      if (parentElId) {
        const $dateRangePickerDiv = document.querySelector(
          `#${parentElId} .daterangepicker`,
        );
        if ($dateRangePickerDiv) {
          $dateRangePickerDiv.style.display = 'flex';
          $dateRangePickerDiv.style.display = 'flex';
        }
      }
    });

    return () => {
      picker.off('apply.daterangepicker');
      picker.off('cancel.daterangepicker');
    };
  }, [sDate, eDate, ranges, t, timePicker, onChange, onClear]);

  useEffect(() => {
    const picker = $(`#${id}`);
    if (picker.length > 0) {
      picker.data('daterangepicker').ranges = ranges;

      const start = sDate ? toRangeMoment(sDate) : toRangeMoment();
      const end = eDate ? toRangeMoment(eDate) : toRangeMoment();

      const matchingRange = Object.keys(ranges).find(key => {
        const [rangeStart, rangeEnd] = ranges[key];
        return (
          start.format('YYYY-MM-DD') === rangeStart.format('YYYY-MM-DD') &&
          end.format('YYYY-MM-DD') === rangeEnd.format('YYYY-MM-DD')
        );
      });

      if (matchingRange) {
        picker.data('daterangepicker').setStartDate(ranges[matchingRange][0]);
        picker.data('daterangepicker').setEndDate(ranges[matchingRange][1]);
        picker.find('.m-input').val(matchingRange);
      } else {
        updateInputValue(picker, start, end);
      }

      if (!sDate && !eDate) {
        $(`#${id} .m-input`).val('');
      }
    }
  }, [ranges, sDate, eDate]);

  return (
    <div className="m-input-icon pull-right" id={id} ref={pickerRef}>
      <input
        type="text"
        className="form-control m-input"
        placeholder={placeholder}
        disabled={loading}
      />
      <span className="m-input-icon__icon m-input-icon__icon--right">
        {loading ? (
          <span
            className="m-loader m-loader--dynamic"
            role="status"
            aria-hidden="true"
          />
        ) : (
          <span>
            <i className="la la-calendar-check-o" />
          </span>
        )}
      </span>
      {planTimezone && (
        <small className="m--font-info d-block text-right">
          {`${t('dateRange_timezone')}: ${planTimezone} (GMT${moment
            .tz(planTimezone)
            .format('Z')})`}
        </small>
      )}
    </div>
  );
}

AsyncDateRange.propTypes = {
  t: PropTypes.func.isRequired,
  id: PropTypes.string,
  startDate: PropTypes.number,
  endDate: PropTypes.number,
  placeholder: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  timePicker: PropTypes.bool,
  opens: PropTypes.string,
  parentElId: PropTypes.string,
};

AsyncDateRange.defaultProps = {
  id: 'asyncDateRangePicker',
  startDate: undefined,
  endDate: undefined,
  placeholder: undefined,
  timePicker: false,
  opens: 'right',
  parentElId: undefined,
};

export default translate(AsyncDateRange);
